import { useEffect } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { useGetBusinessViaIdentifier } from "@/lib/apollo/hooks/booking-edge";
import { usePreserveQueryParams } from "@/hooks/usePreserveQueryParams";

export default function BusinessPageClient({
  businessId,
}: {
  businessId: string;
}) {
  const { navigateWithParams } = usePreserveQueryParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { data, loading, error } = useGetBusinessViaIdentifier(businessId);

  const business = data?.business;
  const salons = business?.salons || [];

  // A shared service, product, or bundle link already points at one specific
  // catalogue item, so those visitors are not asked to pick a location.
  const hasCatalogueDeepLink = Boolean(
    searchParams.get("service") ||
      searchParams.get("product") ||
      searchParams.get("bundle"),
  );

  useEffect(() => {
    // Always redirect to the first salon automatically, preserving query params (e.g., ?source=marketplace)
    if (salons.length > 0 && salons[0].urlIdentifier) {
      const basePath = location.pathname.startsWith("/s/") ? `/s/${businessId}` : `/${businessId}`;
      // Multi-location businesses reached without a service link get the
      // location dialog opened for them once the booking page loads.
      const promptLocationSelection = salons.length > 1 && !hasCatalogueDeepLink;
      navigateWithParams(`${basePath}/${salons[0].urlIdentifier}`, {
        replace: true,
        state: promptLocationSelection ? { promptLocationSelection: true } : undefined,
      });
    }
  }, [salons, businessId, location.pathname, navigateWithParams, hasCatalogueDeepLink]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-grey-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-300 mx-auto"></div>
          <p className="mt-4 text-grey-600">Loading locations...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-grey-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-2">Error loading business</p>
          <p className="text-grey-600 text-sm">{error.message}</p>
        </div>
      </div>
    );
  }

  // No business found
  if (!business) {
    return (
      <div className="min-h-screen bg-grey-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-grey-900 text-xl mb-2">Business not found</p>
          <p className="text-grey-600">Please check the URL and try again</p>
        </div>
      </div>
    );
  }

  // Auto-redirecting to first salon
  if (salons.length > 0) {
    return (
      <div className="min-h-screen bg-grey-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-300 mx-auto"></div>
          <p className="mt-4 text-grey-600">Redirecting...</p>
        </div>
      </div>
    );
  }

  // No salons available
  return (
    <div className="min-h-screen bg-grey-50 flex items-center justify-center">
      <div className="text-center">
        <p className="text-grey-900 text-xl mb-2">No locations available</p>
        <p className="text-grey-600">This business has no active locations</p>
      </div>
    </div>
  );
}
